import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { ContentCardModule } from '@app/library/content-card/content-card.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { PipesModule } from '@core/pipes/pipes.module';
import { ScreenService } from '@core/services';

import { Content } from 'lingo2-models';
import { OnUiCoverModule } from 'onclass-ui';
import { takeUntil } from 'rxjs/operators';
import { AbstractContentPane } from '../models/abstract-content-pane';

import { NewContentCardComponent } from './new-content-card/new-content-card.component';

@Component({
  selector: 'app-new-content',
  templateUrl: './new-content.component.html',
  styleUrls: ['./new-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,
    LibraryElementsModule,

    ModalModule,
    MobileModalModule,
    OnUiCoverModule,
    RouterModule,
    PipesModule,
    ContentCardModule,
    NewContentCardComponent,
  ],
})
export class NewContentComponent extends AbstractContentPane implements OnInit {
  private location = inject(Location);
  private screenService = inject(ScreenService);

  public openedCardIndex: number = null;
  public isSmallScreen = true;
  public reachedPrev = false;
  public reachedNext = false;
  public showModal = false;
  public item: Content = null;
  private previousUrl = 'lessons';

  public ngOnInit() {
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (width) => {
        this.isSmallScreen = this.screenService.isSmallScreen;
      },
      error: (error) => {
        this.isSmallScreen = false;
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
      },
    });
  }

  protected onContentLoaded(viaQuery?: boolean): void {
    this.isVisible = !!this.content?.length;
  }

  public togglePrev(event?) {
    if (this.openedCardIndex - 1 >= 0) {
      this.onOpenCard(this.openedCardIndex - 1);
    }
    this.checkReached();
  }

  public toggleNext(event?) {
    if (this.openedCardIndex + 1 <= this.content.length - 1) {
      this.onOpenCard(this.openedCardIndex + 1);
    }
    this.checkReached();
  }

  public onOpenCard(i) {
    // logger.debug('NewContentComponent:onOpenCard', i);
    this.openedCardIndex = i;
    this.item = this.content[i];
    this.previousUrl = this.location.path();
    if (this.previousUrl) {
      const url = this.contentRoute(this.item);
      this.location.replaceState(url);

      // отправка событий в аналитику для имитации навигации по страницам
      this.analytics.pageView(url, this.previousUrl);
    }
    this.checkReached();
    this.showModal = true;
  }

  public checkReached() {
    const reachedPrev = this.openedCardIndex <= 0;
    const reachedNext = this.openedCardIndex >= this.content.length - 1;

    if (!reachedPrev && !reachedNext) {
      this.reachedPrev = this.reachedNext = false;
    } else if (reachedPrev && reachedNext) {
      this.reachedPrev = this.reachedNext = true;
    } else if (reachedPrev) {
      this.reachedPrev = true;
    } else if (reachedNext) {
      this.reachedNext = true;
    }
  }

  public onCloseCard() {
    // logger.debug('NewContentComponent:onCloseCard');
    this.openedCardIndex = null;
    this.item = null;
    if (this.previousUrl) {
      this.location.replaceState(this.previousUrl);
    }
    // this.showPopover = false;
    this.showModal = false;
  }

  public onCloseModal(event) {
    if (event) {
      this.onCloseCard();
    }
  }

  public trackByFn(index, item: Content): string {
    return item.id;
  }
}
